"use client";

import {
    adminDeleteDocument,
    AdminDeleteDocumentData,
    adminGetDocument,
    AdminGetDocumentData,
    adminUpdateDocument,
    AdminUpdateDocumentData,
} from "@/api";
import { document as documentType } from "@/app/typing/documents";
import { useLocalNumberList } from "@/components/path-params";
import { EntryItem, useRefState } from "@/components/UI/tools";
import { useEffect, useState } from "react";
import { appState } from "@/app/appState";
import { observer } from "mobx-react-lite";
import { Request } from "@/components/CRUD";
import { PageBox } from "@/components/UI/page";
import { Org, User } from "@/components/UI/svgs";
import { shortenLabel } from "../chat/chatObjectEditorWindow/chatObjectEditorWindow";
import { PdfViewer } from "@/components/PdfViewer";
import { TextViewer } from "@/components/TextViewer";

const DocumentEntry = observer(({ id, setId }: { id?: number; setId: (id?: number) => void }) => {
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);
    const [doc, setDoc] = useState<documentType | undefined>(undefined);
    const [deleting, setDeleting] = useState(false);
    const [edit, setEdit] = useState(false);

    const [variableName, setVariableName] = useRefState<string>("");
    const [tagId, setTagId] = useRefState<number | undefined>(undefined);
    const [tagSearch, setTagSearch] = useState<string>("");
    const [tagPageNumber, setTagPageNumber] = useState<number>(0);

    const [ids, setIds] = useLocalNumberList("document_ids");

    const reloadTags = async () => {
        await appState.getTags({ pageNumber: tagPageNumber, search: tagSearch });
    };

    useEffect(() => {
        reloadTags();
    }, [tagPageNumber, tagSearch]);

    const deleteDocumentReq = Request<AdminDeleteDocumentData, any>(
        adminDeleteDocument,
        () => ({
            body: {
                document_id: id!,
            },
        }),
        setDeleting,
        () => {
            appState.getDocuments({ pageNumber: 0, search: "" });
            setId(undefined);
        },
        (error) => {
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorDeleteDocument") });
        },
        () => id != undefined && id > 0,
    );

    const updateDocumentReq = Request<AdminUpdateDocumentData, any>(
        adminUpdateDocument,
        () => ({
            body: {
                id: id!,
                variable_name: variableName.current,
                tag_id: tagId.current,
            },
        }),
        setLoading,
        () => { appState.getDocuments({ pageNumber: 0, search: "" }); },
        (error) => {
            setRefresh((p) => p + 1);
            setEdit(false);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateDocument"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            });
        },
        () => id != undefined && id > 0 && variableName.current.length > 0 && tagId.current !== undefined,
    );

    const getDocumentReq = Request<AdminGetDocumentData, any>(
        adminGetDocument,
        () => ({
            body: {
                id: id!,
            },
        }),
        setLoading,
        (data: any) => {
            setDoc(data.document);
        },
        (error) => {
            setDoc(undefined);
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorGetDocument") });
        },
        () => id != undefined && id > 0,
    );

    useEffect(() => {
        setDoc(undefined);
        if (id && id > 0) getDocumentReq();
    }, [id]);

    const refreshRefs = () => {
        if (doc) {
            setVariableName(doc.variable_name);
            setTagId(doc.tag_id);
        } else {
            setVariableName("");
            setTagId(undefined);
        }
    };

    useEffect(() => {
        refreshRefs();
        setRefresh((prev) => prev + 1);
    }, [doc]);

    if (!doc || doc.id !== id) return (
        <PageBox>
            <div id="document" className="h-full w-full" />
        </PageBox>
    );

    const dateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
    const isPdf = doc.content_type === "application/pdf";
    const isText = doc.content_type === "text/plain";

    return (
        <PageBox>
            <div id="document" className="h-full w-full">
                {isPdf && (
                    <PdfViewer documentId={doc.id} filename={doc.original_filename} />
                )}
                {isText && (
                    <TextViewer documentId={doc.id} filename={doc.original_filename} />
                )}
                <EntryItem
                    lock={false}
                    setEdit={setEdit}
                    edit={edit}
                    name={appState.t("objects.list.document")}
                    created_at={new Date(doc.created_at ?? "").toLocaleDateString(undefined, dateOptions)}
                    loading={loading}
                    deleting={deleting}
                    readonly={
                        ![4, 5, 6, 7].includes(appState.user?.active_organisation.permission_type ?? 0) ||
                        ![4, 5, 6, 7].includes(doc.permission_type ?? 0)
                    }
                    deleteEntry={deleteDocumentReq}
                    updateEntry={updateDocumentReq}
                    refreshCount={refresh}
                    idParams={{
                        singular: { get: id, set: setId },
                        plural: { get: ids, set: setIds },
                    }}
                    nameColumn={{
                        type: "text",
                        value: variableName.current,
                        onChange: setVariableName,
                        validator: (val: string) => !/["'${}`,]/.test(val),
                        label: appState.t("objects.variableName"),
                    }}
                    stateColumns={[
                        {
                            label: "#",
                            type: "single",
                            onChange: (value: number | undefined) => setTagId(value),
                            search: { get: tagSearch, set: setTagSearch },
                            pageNumber: { get: tagPageNumber, set: setTagPageNumber },
                            value: tagId.current,
                            items: appState.tags
                                .filter((x) => [4, 5, 6, 7].includes(x.effective_permission_type))
                                .map((x) => ({
                                    id: x.id,
                                    name: x.name,
                                    children: (
                                        <div className="flex flex-row gap-[0.5rem] items-center">
                                            <>{shortenLabel(x.name)}</>
                                            {x.scope == "me" ? <User size={16} /> : <Org size={16} />}
                                        </div>
                                    ),
                                })),
                            validator: (val: number | undefined) => val !== undefined,
                        },
                    ]}
                />
            </div>
        </PageBox>
    );
});

export default DocumentEntry;
